import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Tenants from './pages/Tenants';
import Settings from './pages/Settings';
import UserManagement from './pages/UserManagement';
import AcademicSession from './pages/AcademicSession';
import CourseManagement from './pages/CourseManagement';
import BOSManagement from './pages/BOSManagement';
import QPSettings from './pages/QPSettings';
import PaperUserAllocations from './pages/PaperUserAllocations';
import QPTemplateLibrary from './pages/QPTemplateLibrary';
import QuestionBank from './pages/QuestionBank';
import QPGeneration from './pages/QPGeneration';
import QPGenerationEditor from './pages/QPGenerationEditor';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  // Force password change if required
  if (user.mustChangePassword) return <Navigate to="/change-password" />;
  return children;
}

function ChangePasswordRoute() {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  // If password already changed, redirect to dashboard
  if (!user.mustChangePassword) return <Navigate to="/dashboard" />;
  return <ChangePassword />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;
  if (!user) return children;
  if (user.mustChangePassword) return <Navigate to="/change-password" />;
  return <Navigate to="/dashboard" />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/change-password" element={<ChangePasswordRoute />} />
      <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route index element={<Navigate to="/dashboard" />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="tenants" element={<Tenants />} />
        <Route path="settings" element={<Settings />} />
        <Route path="user-management" element={<UserManagement />} />
        <Route path="academic-session" element={<AcademicSession />} />
        <Route path="course-management" element={<CourseManagement />} />
        <Route path="bos-management" element={<BOSManagement />} />
        <Route path="qp-settings" element={<QPSettings />} />
        <Route path="paper-user-allocations" element={<PaperUserAllocations />} />
        <Route path="qp-template-library" element={<QPTemplateLibrary />} />
        <Route path="question-bank" element={<QuestionBank />} />
        <Route path="qp-generation" element={<QPGeneration />} />
        <Route path="qp-generation/edit/:paperId" element={<QPGenerationEditor />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
        <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
      </Router>
    </AuthProvider>
  );
}

export default App;
